import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import FormikSelect from 'components/select/formik-select';
import { SelectItem } from 'components/select/types';
import {
  SteelProfileTypeSubtypeItem,
  SteelStructuralElementsValues,
} from 'pages/steel/types';
import {
  getProfileSubtypeSelectOptions,
  getProfileTypeSelectOptions,
} from './logic';

interface StandardProfileInputsProps {
  values: SteelStructuralElementsValues;
  steelProfileTypesSubtypes?: SteelProfileTypeSubtypeItem[];
  selectedShapeId: string | number;
}

const C_METRIC_PROFILE = 'C (metric)';
const C_PROFILE_ID = 2;

const StandardProfileInputs = ({
  values,
  steelProfileTypesSubtypes, selectedShapeId
}: StandardProfileInputsProps) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();
  const [profileSubtypes, setProfileSubtypes] = useState<SelectItem[]>();

  const profileTypes = getProfileTypeSelectOptions(steelProfileTypesSubtypes);

  useEffect(() => {
    if (
      profileTypes?.length === 1 &&
      values.profileType !== String(profileTypes[0].id)
    )
      setFieldValue('profileType', String(profileTypes[0].id));

    const profileSubtypes = getProfileSubtypeSelectOptions(
      values.profileType,
      steelProfileTypesSubtypes
    );

    setProfileSubtypes(profileSubtypes);
  }, [profileTypes, values.profileType]);

  const mapProfileTypeTranslations = (profileTypes: SelectItem[]) => {
    if (profileTypes?.length === 1) {
      return 'steel.shape.hollowShapesPlaceholders';
    } else if (profileTypes?.length === 2) {
      return 'steel.shape.angleLineTypes';
    }
  };

  const mapCMetricTranslation = (profileTypes: SelectItem[]) => {
    if (selectedShapeId === C_PROFILE_ID) {
      return profileTypes.map(type => ({
        ...type,
        name: type.name === C_METRIC_PROFILE ? t('steel.shape.cMetric') ?? C_METRIC_PROFILE : type.name,
      }));
    }
    return profileTypes;
  };

  return (
    <>
      {profileTypes && (
        <FormikSelect
          name="profileType"
          label={t('steel.shape.type') ?? ''}
          emptyValue={t('steel.shape.typeEmpty') ?? ''}
          items={selectedShapeId === C_PROFILE_ID ? mapCMetricTranslation(profileTypes) : profileTypes }
          translationKey={mapProfileTypeTranslations(profileTypes)}
        />
      )}
      {profileSubtypes && (
        <FormikSelect
          name="profileSubtype"
          label={t('steel.shape.subtype') ?? ''}
          emptyValue={t('steel.shape.subtypeEmpty') ?? ''}
          items={profileSubtypes}
        />
      )}
    </>
  );
};

export default StandardProfileInputs;
